/* eslint-disable @typescript-eslint/no-unused-vars */
import isUndefined from 'lodash/isUndefined'
import { useMemo, useCallback, Suspense, useEffect } from 'react'
import { Form, FormSpy } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'

import { FormProps } from '~/common/types/form'
import FormField from '~/form/components/FormField'
import Rows from '~/form/components/atoms/Rows'
import ApiError from '~/form/components/molecules/ApiError'
import CheckBox from '~/form/components/molecules/CheckBox'
import ConfirmNotification from '~/form/components/molecules/ConfirmNotification'
import FormButtonSet from '~/form/components/organisms/FormButtonSet'
import LinkArea from '~/form/components/organisms/LinkArea'
import configs from '~/form/configs'
import useCreateSendParam from '~/form/hooks/useCreateSendParam'
import createFormValidate from '~/form/libs/createFormValidate'
import createInitialValues from '~/form/libs/createInitialValues'
import { usePostMutation } from '~/form/redux/apiSlice'
import { setPage } from '~/form/redux/formSlice'

import saveFormValuesToStorage from '../libs/saveFormValuesToStorage'
import { WorkerFormStorage } from '../types'

const PageForm = ({
  workerFormValues,
}: {
  workerFormValues?: WorkerFormStorage
}) => {
  const dispatch = useDispatch()

  const reduxPage = useSelector((state) => state.form.page)

  const reduxFormType = useSelector((state) => state.form.formType)

  const reduxClientId = useSelector((state) => state.form.clientId)

  const reduxFormName = useSelector((state) => state.form.formName)

  const reduxEmploymentStatus = useSelector(
    (state) => state.form.employmentStatus
  )

  const createSendParam = useCreateSendParam(
    reduxFormType,
    reduxClientId,
    reduxFormName,
    reduxEmploymentStatus
  )

  const [post, { isLoading, error: postError, isSuccess, isError }] =
    usePostMutation()

  const isConfirm = useMemo(() => reduxPage === 'confirm', [reduxPage])

  const handleBack = useCallback(() => {
    dispatch(setPage('input'))
  }, [dispatch])

  const handleSubmit = useCallback(
    async (values: FormProps) => {
      if (reduxPage === 'input') {
        // 入力画面
        dispatch(setPage('confirm'))
      }
      if (reduxPage === 'confirm') {
        // 確認画面
        const postUrl = (() => {
          switch (reduxFormType) {
            case 'REGISTER':
              return 'register'
            case 'REPOST':
              return 'repost'
            default:
              return 'applicant'
          }
        })()
        saveFormValuesToStorage(values)
        const sendParam = createSendParam(values)
        await post({ postUrl, ...sendParam })
      }
    },
    [createSendParam, dispatch, post, reduxFormType, reduxPage]
  )

  useEffect(() => {
    if (isSuccess) {
      dispatch(setPage('thanks'))
    }
  }, [dispatch, isSuccess])

  useEffect(() => {
    if (isError) {
      dispatch(setPage('input'))
    }
  }, [dispatch, isError])

  if (isUndefined(reduxPage) || isUndefined(reduxFormType)) return null

  return (
    <div>
      {isError && (
        <div style={{ marginBottom: '20px' }}>
          <ApiError error={postError} />
        </div>
      )}
      {isConfirm && (
        <div style={{ marginBottom: '20px' }}>
          <ConfirmNotification />
        </div>
      )}

      <Form
        initialValues={createInitialValues(workerFormValues)}
        onSubmit={handleSubmit}
        validate={(values) =>
          createFormValidate({ values, formTypeId: reduxFormType })
        }
      >
        {({ handleSubmit }) => {
          return (
            <form onSubmit={handleSubmit} noValidate autoComplete='nope'>
              <FormSpy
                subscription={{
                  submitFailed: true,
                  submitSucceeded: true,
                }}
                onChange={(props) => {
                  if (props.submitFailed) {
                    // バリデーションエラー時は最初のエラー箇所までスクロール
                    setTimeout(() => {
                      const els = document.querySelectorAll('.validation-error')
                      if (els && els.length) {
                        els[0].scrollIntoView({ behavior: 'smooth' })
                      }
                    }, 0)
                  }
                  if (props.submitSucceeded) {
                    // 送信成功時はフォームの先頭位置までスクロール
                    setTimeout(() => {
                      const el = document.querySelector('#form_confirm')
                      if (el) {
                        el.scrollIntoView()
                      }
                    }, 0)
                  }
                }}
              />
              <Rows>
                <Suspense fallback={<></>}>
                  <FormField
                    isConfirm={isConfirm}
                    configs={configs[reduxFormType]}
                  />
                </Suspense>

                {reduxFormType !== 'REGISTER' && (
                  <>
                    {/* メルマガ購読チェック */}
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <CheckBox
                        label='サクサク仕事探しからのお知らせを受け取る'
                        name='acceptMailMagazine'
                        isConfirm={isConfirm}
                        initialValue={true}
                      />
                    </div>
                    {/* メルマガ購読チェック */}
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <CheckBox
                        label='応募した内容でスカウト登録代行を依頼する'
                        name='requestForRegistration'
                        isConfirm={isConfirm}
                        initialValue={true}
                      />
                    </div>
                  </>
                )}
                {reduxPage === 'input' && <LinkArea />}
                <FormButtonSet
                  onBack={handleBack}
                  page={reduxPage}
                  isLoading={isLoading}
                />
              </Rows>
            </form>
          )
        }}
      </Form>
    </div>
  )
}

export default PageForm
